import { admin2 } from '@/api/instance'

/**
 * @typedef {Object} Info
 * @property {string} name
 * @property {string} code
 * @property {string} address
 * @property {string} phone
 * @property {string} contactName
 * @property {string} contactMobile
 */

/**
 * @typedef {Object} MemberCustomProfile
 * @property {string} id
 * @property {string} type
 * @property {string} code
 * @property {string} name
 * @property {{ [key: string]: Info }} info
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string} ShopId
 * @property {string} MemberId
 */

/**
 * 建立會員自訂檔案的基礎URL
 * @param {string} shopId - 商店ID
 * @returns {string} 完整的API路徑
 */
const baseUrl = (shopId) => {
  return `/${shopId}/memberCustomProfile`
}

/**
 * 查找會員自訂檔案列表 admin.memberCustomProfile.find
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID (Path 參數)
 * @param {string} [params.type] - 檔案類型 (Query 參數)
 * @param {string} [params.code] - 紀錄代碼 (Query 參數)
 * @param {string} [params.name] - 紀錄名稱 (Query 參數)
 * @param {number} [params.start] - 起始位置 (Query 參數)
 * @param {number} [params.limit] - 取得筆數 (Query 參數)
 * @param {string} [params.MemberId] - 會員ID (Query 參數)
 * @returns {Promise<Array<MemberCustomProfile>>} 會員自訂檔案列表資料
 */
export const FindMemberCustomProfile = async ({
  shopId,
  type,
  code,
  name,
  start,
  limit,
  MemberId,
}) => {
  const res = await admin2({
    method: 'get',
    url: `${baseUrl(shopId)}`,
    params: {
      type,
      code,
      name,
      start,
      limit,
      MemberId,
    },
  })

  return res
}

/**
 * 新增會員自訂檔案 admin.memberCustomProfile.create
 * @param {Object} params - 建立參數
 * @param {string} params.shopId - 商店ID (Path 參數)
 * @param {string} params.type - 檔案類型
 * @param {string} params.code - 紀錄代碼
 * @param {string} params.name - 紀錄名稱
 * @param {Object} params.info - 詳細資訊（如 Info 物件）
 * @param {string} params.MemberId - 會員ID
 * @returns {Promise<MemberCustomProfile>} 新增的會員自訂檔案資料
 */
export const CreateMemberCustomProfile = async ({
  shopId,
  type,
  code,
  name,
  info,
  MemberId,
}) => {
  const res = await admin2({
    method: 'post',
    url: `${baseUrl(shopId)}`,
    data: {
      type,
      code,
      name,
      info,
      MemberId,
    },
  })

  return res
}

/**
 * 取得會員自訂檔案總數 admin.memberCustomProfile.count
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID (Path 參數)
 * @param {string} [params.type] - 檔案類型 (Query 參數)
 * @param {string} [params.code] - 紀錄代碼 (Query 參數)
 * @param {string} [params.name] - 紀錄名稱 (Query 參數)
 * @param {string} [params.MemberId] - 會員ID (Query 參數)
 * @returns {Promise<{ count: number }>} 會員自訂檔案總數
 */
export const GetMemberCustomProfileCount = async ({
  shopId,
  type,
  code,
  name,
  MemberId,
}) => {
  const res = await admin2({
    method: 'get',
    url: `${baseUrl(shopId)}/count`,
    params: {
      type,
      code,
      name,
      MemberId,
    },
  })

  return res
}

/**
 * 查找指定會員自訂檔案 admin.memberCustomProfile.findOne
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID (Path 參數)
 * @param {string} params.id - 會員自訂檔案ID
 * @returns {Promise<MemberCustomProfile>} 指定的會員自訂檔案資料
 */
export const FindOneMemberCustomProfile = async ({
  shopId,
  id,
}) => {
  const res = await admin2({
    method: 'get',
    url: `${baseUrl(shopId)}/${id}`,
  })

  return res
}

/**
 * 更新會員自訂檔案 admin.memberCustomProfile.update
 * @param {Object} params - 更新參數
 * @param {string} params.shopId - 商店ID (Path 參數)
 * @param {string} params.id - 會員自訂檔案ID (Path 參數)
 * @param {string} params.type - 檔案類型
 * @param {string} params.code - 紀錄代碼
 * @param {string} params.name - 紀錄名稱
 * @param {Object} params.info - 詳細資訊（如 Info 物件）
 * @returns {Promise<Object>} 更新後的會員自訂檔案資料
 */
export const UpdateMemberCustomProfile = async ({
  shopId,
  id,
  type,
  code,
  name,
  info,
}) => {
  const res = await admin2({
    method: 'put',
    url: `${baseUrl(shopId)}/${id}`,
    data: {
      type,
      code,
      name,
      info,
    },
  })

  return res
}
